import { isLeft, isRight } from 'fp-ts/Either';
import { useCallback, useEffect, useState } from 'react';

import type { TRPC } from '../../contexts/TRPC';
import { captureAndReportError } from '../../utils/errorReporting';
import type {
  ApiResult,
  CandidateRun,
  DappReviewsResult,
  Experiment,
  ExperimentCreatePayload,
  GenerateCandidatesPayload,
  ReportingQuery,
  ReportingSummary,
  ReviewSort,
  Spotlight,
  SpotlightAppChoicesOutput,
  SpotlightAppOption,
  SpotlightSavePayload,
} from './types';

interface UseAppSpotlightOptions {
  trpc: TRPC['client'];
  onError: (message: string) => void;
}

const APP_SPOTLIGHT_REVIEW_PAGE_SIZE = 10;

function leftResult(message: string): ApiResult<never> {
  return { _tag: 'Left', left: { message } };
}

function normalizeSpotlight(
  value: Spotlight | { spotlight: Spotlight },
): Spotlight {
  return 'spotlight' in value ? value.spotlight : value;
}

export function useAppSpotlight({ trpc, onError }: UseAppSpotlightOptions) {
  const [spotlights, setSpotlights] = useState<Spotlight[]>([]);
  const [candidateRuns, setCandidateRuns] = useState<CandidateRun[]>([]);
  const [experiments, setExperiments] = useState<Experiment[]>([]);
  const [appOptions, setAppOptions] = useState<SpotlightAppOption[]>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingApps, setIsLoadingApps] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRunningPersonalization, setIsRunningPersonalization] =
    useState(false);

  async function runMutation<T>(
    operation: string,
    fn: () => Promise<unknown>,
  ): Promise<ApiResult<T>> {
    try {
      return (await fn()) as ApiResult<T>;
    } catch (error) {
      captureAndReportError(error, { operation, component: 'useAppSpotlight' });
      return leftResult('Request failed. Please try again.');
    }
  }

  const fetchSpotlights = useCallback(async () => {
    const all: Spotlight[] = [];
    const limit = 100;
    let offset = 0;

    for (;;) {
      const result = (await trpc.listAppSpotlights.query({
        status: 'all',
        limit,
        offset,
      })) as ApiResult<{ spotlights: Spotlight[]; total: number }>;

      if (isLeft(result)) {
        onError(result.left.message);
        return;
      }

      all.push(...result.right.spotlights);

      const fetchedAll =
        result.right.spotlights.length < limit ||
        all.length >= result.right.total;
      if (fetchedAll || offset >= 5000) {
        break;
      }
      offset += limit;
    }

    setSpotlights(all);
  }, [trpc, onError]);

  const fetchCandidateRuns = useCallback(async () => {
    const result = (await trpc.listAppSpotlightCandidateRuns.query({
      limit: 20,
      offset: 0,
    })) as ApiResult<{ runs: CandidateRun[] }>;
    if (isRight(result)) {
      setCandidateRuns(result.right.runs);
    } else {
      onError(result.left.message);
    }
  }, [trpc, onError]);

  const fetchExperiments = useCallback(async () => {
    const result = (await trpc.listAppSpotlightExperiments.query({
      limit: 50,
      offset: 0,
    })) as ApiResult<{ experiments: Experiment[] }>;
    if (isRight(result)) {
      setExperiments(result.right.experiments);
    } else {
      onError(result.left.message);
    }
  }, [trpc, onError]);

  const loadAppOptions = useCallback(async () => {
    setIsLoadingApps(true);
    try {
      const dapps: SpotlightAppOption[] = [];
      let page = 1;
      let totalPages = 1;

      do {
        const result = (await trpc.listAllDapps.query({
          filters: { status: 'all' },
          pagination: { page, limit: 100 },
          sorting: [{ id: 'dappName', desc: false }],
        })) as ApiResult<SpotlightAppChoicesOutput>;

        if (isLeft(result)) {
          onError(result.left.message);
          return;
        }

        dapps.push(...result.right.dapps);
        totalPages = result.right.pagination.totalPages;
        page += 1;
      } while (page <= totalPages);

      setAppOptions(dapps);
    } catch (error) {
      captureAndReportError(error, {
        operation: 'admin.loadAppSpotlightApps',
        component: 'useAppSpotlight',
      });
      onError('Failed to load app choices');
    } finally {
      setIsLoadingApps(false);
    }
  }, [trpc, onError]);

  const reload = useCallback(async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        fetchSpotlights(),
        fetchCandidateRuns(),
        fetchExperiments(),
      ]);
    } catch (error) {
      captureAndReportError(error, {
        operation: 'admin.loadAppSpotlight',
        component: 'useAppSpotlight',
      });
      onError('Failed to load App Spotlight workspace');
    } finally {
      setIsLoading(false);
    }
  }, [fetchSpotlights, fetchCandidateRuns, fetchExperiments, onError]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    void loadAppOptions();
  }, [loadAppOptions]);

  const upsertSpotlight = (spotlight: Spotlight) => {
    setSpotlights((current) => {
      const exists = current.some((item) => item.id === spotlight.id);
      return exists
        ? current.map((item) => (item.id === spotlight.id ? spotlight : item))
        : [spotlight, ...current];
    });
  };

  const saveSpotlight = async (
    payload: SpotlightSavePayload,
  ): Promise<ApiResult<Spotlight>> => {
    setIsSaving(true);
    try {
      const { id, ...rest } = payload;
      const result = await runMutation<Spotlight>(
        'admin.saveAppSpotlight',
        () =>
          id
            ? trpc.updateAppSpotlightDraft.mutate({ ...rest, id })
            : trpc.createAppSpotlightDraft.mutate(rest),
      );
      if (isRight(result)) {
        upsertSpotlight(result.right);
      }
      return result;
    } finally {
      setIsSaving(false);
    }
  };

  const setStatus = async (
    id: string,
    op: 'publish' | 'archive' | 'unarchive',
    confirmationText?: string,
  ): Promise<ApiResult<Spotlight>> => {
    const operation = `admin.${op}AppSpotlight`;
    const result = await runMutation<Spotlight | { spotlight: Spotlight }>(
      operation,
      () => {
        if (op === 'archive') {
          return trpc.archiveAppSpotlight.mutate({ id, confirmationText });
        }
        if (op === 'unarchive') {
          return trpc.unarchiveAppSpotlight.mutate({ id });
        }
        return trpc.publishAppSpotlight.mutate({ id });
      },
    );
    if (isLeft(result)) {
      return result;
    }
    const spotlight = normalizeSpotlight(result.right);
    upsertSpotlight(spotlight);
    return { _tag: 'Right', right: spotlight };
  };

  const updatePublishedSpotlight = async (
    payload: SpotlightSavePayload,
  ): Promise<ApiResult<Spotlight>> => {
    if (!payload.id) {
      return leftResult('Save the spotlight first');
    }
    setIsSaving(true);
    try {
      const { id, ...rest } = payload;
      const result = await runMutation<Spotlight | { spotlight: Spotlight }>(
        'admin.updatePublishedAppSpotlight',
        () => trpc.updatePublishedAppSpotlight.mutate({ ...rest, id }),
      );
      if (isLeft(result)) {
        return result;
      }
      const spotlight = normalizeSpotlight(result.right);
      upsertSpotlight(spotlight);
      return { _tag: 'Right', right: spotlight };
    } finally {
      setIsSaving(false);
    }
  };

  const generateCandidates = async (
    payload: GenerateCandidatesPayload,
  ): Promise<ApiResult<CandidateRun>> => {
    setIsGenerating(true);
    try {
      const result = await runMutation<CandidateRun>(
        'admin.createAppSpotlightCandidateRun',
        () =>
          trpc.createAppSpotlightCandidateRun.mutate({
            weekId: payload.weekId,
            themeTitle: payload.themeTitle,
            limit: 12,
            excludedAndroidPackages: payload.excludedAndroidPackages,
          }),
      );
      if (isRight(result)) {
        setCandidateRuns((current) => [result.right, ...current]);
      }
      return result;
    } finally {
      setIsGenerating(false);
    }
  };

  const createExperiment = async (
    payload: ExperimentCreatePayload,
  ): Promise<ApiResult<Experiment>> => {
    const result = await runMutation<Experiment>(
      'admin.createAppSpotlightExperiment',
      () => trpc.createAppSpotlightExperiment.mutate(payload),
    );
    if (isRight(result)) {
      setExperiments((current) => [result.right, ...current]);
    }
    return result;
  };

  const approveExperiment = async (
    experimentDbId: string,
  ): Promise<ApiResult<Experiment>> => {
    const result = await runMutation<Experiment>(
      'admin.approveAppSpotlightExperiment',
      () => trpc.approveAppSpotlightExperiment.mutate({ experimentDbId }),
    );
    if (isRight(result)) {
      setExperiments((current) =>
        current.map((item) =>
          item.id === result.right.id ? result.right : item,
        ),
      );
    }
    return result;
  };

  const runPersonalization = async (
    experimentDbId: string,
    audienceMode: 'limited' | 'all',
    userLimit?: number,
  ): Promise<ApiResult<unknown>> => {
    setIsRunningPersonalization(true);
    try {
      const result = await runMutation<unknown>(
        'admin.runAppSpotlightPersonalizationExperiment',
        () =>
          trpc.runAppSpotlightPersonalizationExperiment.mutate({
            experimentDbId,
            audienceMode,
            userLimit: audienceMode === 'limited' ? userLimit : undefined,
          }),
      );
      if (isRight(result)) {
        await fetchExperiments();
      }
      return result;
    } finally {
      setIsRunningPersonalization(false);
    }
  };

  const loadReporting = async (
    query: ReportingQuery,
  ): Promise<ApiResult<ReportingSummary[]>> => {
    const result = await runMutation<{ summaries: ReportingSummary[] }>(
      'admin.getAppSpotlightReporting',
      () => trpc.getAppSpotlightReporting.query(query),
    );
    if (isLeft(result)) {
      return result;
    }
    return { _tag: 'Right', right: result.right.summaries };
  };

  const fetchDappReviews = useCallback(
    async (
      dappId: string,
      options: { page: number; sortBy: ReviewSort },
    ): Promise<ApiResult<DappReviewsResult>> => {
      try {
        return (await trpc.getDappRatingsReviews.query({
          dappId,
          page: options.page,
          first: APP_SPOTLIGHT_REVIEW_PAGE_SIZE,
          sortBy: options.sortBy,
        })) as ApiResult<DappReviewsResult>;
      } catch (error) {
        captureAndReportError(error, {
          operation: 'admin.getDappRatingsReviews',
          component: 'useAppSpotlight',
        });
        return leftResult('Failed to load reviews. Please try again.');
      }
    },
    [trpc],
  );

  return {
    spotlights,
    candidateRuns,
    experiments,
    appOptions,
    isLoading,
    isLoadingApps,
    isSaving,
    isGenerating,
    isRunningPersonalization,
    reload,
    reloadExperiments: fetchExperiments,
    reloadCandidateRuns: fetchCandidateRuns,
    saveSpotlight,
    setStatus,
    updatePublishedSpotlight,
    generateCandidates,
    createExperiment,
    approveExperiment,
    runPersonalization,
    loadReporting,
    fetchDappReviews,
  };
}

export type AppSpotlightController = ReturnType<typeof useAppSpotlight>;
